import { useAgencyAssets } from '../contexts/AgencyAssetsContext'
import { DynamicKoproLogo } from './DynamicKoproLogo'

interface AgencyLogoProps {
  variant: 'compact' | 'inline'
  height?: string
  alt?: string
  style?: React.CSSProperties
}

/**
 * AgencyLogo - Displays the agency logo (compact or inline)
 *
 * Automatically falls back to dynamic Kopro logo (with agency brand color) if agency logo is not configured
 *
 * Usage:
 * ```tsx
 * <AgencyLogo variant="compact" height="60px" />
 * <AgencyLogo variant="inline" height="80px" />
 * ```
 */
export function AgencyLogo({ variant, height = '60px', alt, style }: AgencyLogoProps) {
  const { assets, agencyName } = useAgencyAssets()

  const logoUrl =
    variant === 'compact' ? assets.logoCompactUrl : assets.logoInlineUrl

  const logoAlt = alt || `${agencyName} - ${variant === 'compact' ? 'Logo' : 'Logo complet'}`

  // If agency has uploaded a custom logo, display it
  if (logoUrl) {
    // Handle image load error by falling back to dynamic Kopro logo
    const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
      // Hide the broken image and show the fallback
      e.currentTarget.style.display = 'none'
      const fallback = e.currentTarget.nextElementSibling as HTMLElement
      if (fallback) {
        fallback.style.display = 'block'
      }
    }

    return (
      <>
        <img
          key={logoUrl} // Force re-render when URL changes
          src={logoUrl}
          alt={logoAlt}
          onError={handleError}
          style={{
            height,
            width: 'auto',
            objectFit: 'contain',
            ...style,
          }}
        />
        {/* Fallback dynamic logo (hidden by default) */}
        <div style={{ display: 'none' }}>
          <DynamicKoproLogo variant={variant} height={height} />
        </div>
      </>
    )
  }

  // No custom logo uploaded, display dynamic Kopro logo with agency brand color
  return <DynamicKoproLogo variant={variant} height={height} />
}
